import React, { useState, useMemo } from "react";
import { useFetch } from "@/lib/api";
import { PageHeader, Panel, StatusChip, LoadingState, CopyButton } from "@/components/app/primitives";

export default function QuestionBuilderPage() {
  const { data: questions, loading } = useFetch("/questions");
  const [audience, setAudience] = useState("all");
  const [week1Only, setWeek1Only] = useState(false);

  const audiences = useMemo(() => [...new Set((questions || []).map((q) => q.audience))], [questions]);

  if (loading) return <LoadingState message="Assembling question packs..." />;

  const filtered = (questions || []).filter((q) => (audience === "all" || q.audience === audience) && (!week1Only || q.week1));

  const guideText = () => {
    const byAud = {};
    (questions || []).filter((q) => q.week1).forEach((q) => { byAud[q.audience] = byAud[q.audience] || []; byAud[q.audience].push(q); });
    let out = "WEEK 1 INTERVIEW GUIDE - SIX x BRIGHTSPEED\nOrder Cancellation Rate Assessment (Illustrative Demo Data)\n\n";
    Object.entries(byAud).forEach(([aud, qs]) => {
      out += `=== ${aud.toUpperCase()} ===\n`;
      qs.forEach((q, i) => {
        out += `${i + 1}. ${q.question}\n   Why: ${q.why}\n   Evidence gap: ${q.evidence_gap} | Decision: ${q.decision_supported} | Metric: ${q.metric}\n   Interviewer: ${q.interviewer} | Output: ${q.artifact}\n\n`;
      });
    });
    return out;
  };

  return (
    <div data-testid="questions-page">
      <PageHeader
        kicker="Scene 7 - Question builder"
        title="Question & Working Session Builder"
        subtitle="Stakeholder-specific questions with the discipline attached: why we ask, the evidence gap it fills, the decision it supports, and the artifact it produces."
        right={<CopyButton text={guideText()} label="Copy Week 1 interview guide" testId="copy-week1-guide-button" />}
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setAudience("all")} data-testid="question-audience-all"
          className={`rounded-lg px-3 py-1.5 text-xs font-medium border transition-colors ${audience === "all" ? "border-[--six-crimson] bg-[--six-crimson-soft] text-[--tx-1]" : "border-[--hairline] bg-[--panel-dark] text-[--tx-2] hover:text-[--tx-1]"}`}>
          All audiences
        </button>
        {audiences.map((a) => (
          <button key={a} onClick={() => setAudience(a)} data-testid={`question-audience-${a.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium border transition-colors ${audience === a ? "border-[--six-crimson] bg-[--six-crimson-soft] text-[--tx-1]" : "border-[--hairline] bg-[--panel-dark] text-[--tx-2] hover:text-[--tx-1]"}`}>
            {a}
          </button>
        ))}
        <button onClick={() => setWeek1Only(!week1Only)} data-testid="question-week1-filter"
          className={`ml-auto rounded-lg px-3 py-1.5 text-xs font-semibold border transition-colors ${week1Only ? "border-[--amber] bg-[--amber-soft] text-[--amber]" : "border-[--hairline] bg-[--panel-dark] text-[--tx-2] hover:text-[--tx-1]"}`}>
          Week 1 required only
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3" data-testid="question-list">
        {filtered.map((q) => (
          <div key={q.id} className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-3.5" data-testid={`question-card-${q.id}`}>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="font-mono text-[10px] text-[--tx-3]">{q.id}</span>
              <StatusChip tone="neutral">{q.audience}</StatusChip>
              {q.week1 && <StatusChip tone="risk">Week 1</StatusChip>}
            </div>
            <p className="text-[13px] font-medium text-[--tx-1] leading-relaxed mb-2.5">{q.question}</p>
            <div className="space-y-1 text-[11px]">
              <div className="flex gap-2"><span className="text-[--tx-3] w-28 shrink-0">Why we ask</span><span className="text-[--tx-2]">{q.why}</span></div>
              <div className="flex gap-2"><span className="text-[--tx-3] w-28 shrink-0">Evidence gap</span><span className="text-[--ai] font-mono text-[10px] pt-px">{q.evidence_gap}</span></div>
              <div className="flex gap-2"><span className="text-[--tx-3] w-28 shrink-0">Decision supported</span><span className="text-[--tx-2]">{q.decision_supported}</span></div>
              <div className="flex gap-2"><span className="text-[--tx-3] w-28 shrink-0">Related metric</span><span className="text-[--tx-2]">{q.metric}</span></div>
              <div className="flex gap-2"><span className="text-[--tx-3] w-28 shrink-0">Interviewer</span><span className="text-[--tx-2]">{q.interviewer}</span></div>
              <div className="flex gap-2"><span className="text-[--tx-3] w-28 shrink-0">Expected artifact</span><span className="text-[--green]">{q.artifact}</span></div>
            </div>
          </div>
        ))}
      </div>

      <Panel title="Working-session outputs available" className="mt-4" testId="question-outputs-panel">
        <div className="grid md:grid-cols-4 gap-3">
          {[
            { t: "Week 1 interview guide", d: "All Week 1 questions by audience with rationale - copy above" },
            { t: "Working-session agenda", d: "90-min session frame: evidence review, gap walk, decision asks, next actions" },
            { t: "Stakeholder question pack", d: "Filter by audience, then copy - each pack self-contained with artifacts expected" },
            { t: "Follow-up request list", d: "Every evidence-gap reference rolls into the Evidence Room request tracker" },
          ].map((o) => (
            <div key={o.t} className="rounded-lg bg-[--shell-2] border border-[--divider] p-3">
              <div className="text-xs font-semibold text-[--tx-1] mb-1">{o.t}</div>
              <div className="text-[11px] text-[--tx-3] leading-snug">{o.d}</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
